/**
 * useBannerAnimation Hook
 *
 * State machine for the banner logo animation sequence:
 *   OPEN:  idle → rotating → sliding-open → open
 *   CLOSE: open → sliding-closed → unrotating → idle
 *
 * Supports two slide modes:
 *   'dialog' — partial open (360px) for column/final answer dialogs
 *   'full'   — full open (570px) for post-game explanation panel (logos off-screen)
 *
 * Uses CSS transitions with onTransitionEnd for definitive step completion.
 * Each step triggers ONLY when the previous step's transition actually finishes.
 */

import { useState, useCallback, useRef } from 'react';

// Configurable animation parameters
export const ROTATE_DURATION = 300; // ms — plus sign rotation
export const SLIDE_DURATION = 500;  // ms — logo halves slide apart/together
export const SLIDE_DISTANCE_DIALOG = 360;  // px — partial open for dialogs
export const SLIDE_DISTANCE_FULL = 570;    // px — full open, logos completely off-screen

export type BannerPhase =
  | 'idle'
  | 'rotating'
  | 'sliding-open'
  | 'open'
  | 'sliding-closed'
  | 'unrotating';

export type BannerMode = 'dialog' | 'full';

export function useBannerAnimation() {
  const [phase, setPhase] = useState<BannerPhase>('idle');
  const modeRef = useRef<BannerMode>('dialog');
  // Override slide duration for board open/close sync (null = use default per-mode duration)
  const slideDurationOverrideRef = useRef<number | null>(null);
  const open = useCallback(() => {
    modeRef.current = 'dialog';
    setPhase(p => {
      if (p === 'idle') return 'rotating';
      // If closing, jump straight to open
      if (p === 'sliding-closed' || p === 'unrotating') return 'open';
      return p; // already opening or open
    });
  }, []);

  const openFull = useCallback(() => {
    modeRef.current = 'full';
    setPhase(p => {
      if (p === 'idle') return 'rotating';
      // If closing, jump straight to open
      if (p === 'sliding-closed' || p === 'unrotating') return 'open';
      return p; // already opening or open
    });
  }, []);

  const close = useCallback(() => {
    setPhase(p => {
      if (p === 'open') return 'sliding-closed';
      if (p === 'sliding-open') return 'sliding-closed';
      return p; // already closing or closed
    });
  }, []);

  const onPlusTransitionEnd = useCallback(() => {
    setPhase(p => {
      if (p === 'rotating') return 'sliding-open';
      if (p === 'unrotating') {
        // Reset mode to dialog when fully closed
        modeRef.current = 'dialog';
        return 'idle';
      }
      return p;
    });
  }, []);

  const onSlideTransitionEnd = useCallback(() => {
    setPhase(p => {
      if (p === 'sliding-open') return 'open';
      if (p === 'sliding-closed') return 'unrotating';
      return p;
    });
  }, []);

  // Dialog is visible once logos start opening, until they fully close
  const dialogVisible =
    phase === 'sliding-open' || phase === 'open' || phase === 'sliding-closed';

  // Plus sign: rotation target, visibility, and whether transition is active
  const plusRotation = (phase === 'idle' || phase === 'unrotating') ? 0 : -180;
  const plusVisible = phase === 'idle' || phase === 'rotating' || phase === 'unrotating';
  const plusTransitioning = phase === 'rotating' || phase === 'unrotating';

  // Logo slide: offset target and whether transition is active
  const slideDistance = modeRef.current === 'full' ? SLIDE_DISTANCE_FULL : SLIDE_DISTANCE_DIALOG;
  const slideOffset = (phase === 'sliding-open' || phase === 'open') ? slideDistance : 0;
  const slideTransitioning = phase === 'sliding-open' || phase === 'sliding-closed';
  const slideDuration = slideDurationOverrideRef.current ?? SLIDE_DURATION;

  // Set slide duration override (for board open/close sync). Pass null to reset to default.
  const setSlideDuration = useCallback((ms: number | null) => {
    slideDurationOverrideRef.current = ms;
  }, []);

  return {
    phase,
    dialogVisible,
    open,
    openFull,
    close,
    onPlusTransitionEnd,
    onSlideTransitionEnd,
    plusRotation,
    plusVisible,
    plusTransitioning,
    slideOffset,
    slideTransitioning,
    slideDuration,
    setSlideDuration,
    bannerMode: modeRef.current,
  };
}
